import { FloatingMenu } from "@tiptap/react"
import { Button, HStack } from "@chakra-ui/react"
import { Editor } from "@tiptap/react"
import { useEffect, useState } from "react"

export function FloatingDiffMenu({ editor }: { editor: Editor }) {
  const [diffId, setDiffId] = useState<string | null>(null)

  /* -------------------------------------------------
     1) Capture the diffId under the current selection
  ------------------------------------------------- */
  const updateDiffId = () => {
    const { selection, doc } = editor.state
    if (!selection.empty) {
      setDiffId(null)
      return
    }
    const $pos = doc.resolve(selection.from)
    const mark = $pos.marks().find(m =>
      (m.type.name === "diffAdded" || m.type.name === "diffRemoved") &&
      m.attrs?.diffId != null
    )
    setDiffId(mark ? (mark.attrs.diffId as string) : null)
  }

  /* run on every editor update */
  useEffect(() => {
    if (!editor) return
  
    // 구독
    editor.on("selectionUpdate", updateDiffId)
  
    // cleanup → void 반환
    return () => {
      editor.off("selectionUpdate", updateDiffId)
      // 아무 것도 반환하지 않음 = void
    }
  }, [editor])

  /* -------------------------------------------------
     2) shouldShow relies exclusively on selection marks
  ------------------------------------------------- */
  const shouldShowMenu = ({ editor }: { editor: Editor }) => {
    const { selection, doc } = editor.state
    if (!selection.empty) return false
    const $pos = doc.resolve(selection.from)
    return $pos.marks().some(m =>
      m.type.name === "diffAdded" || m.type.name === "diffRemoved"
    )
  }

  /* -------------------------------------------------
     3) Helper to collect nodes by current diffId
  ------------------------------------------------- */
  const collectById = (id: string) => {
    const { doc } = editor.state
    let from = 0, to = 0
    let removed = "", added = "", hasR = false, hasA = false
    doc.descendants((node, pos) => {
      if (!node.isText) return
      node.marks.forEach(m => {
        if (m.attrs?.diffId === id) {
          if (from === 0) from = pos
          to = pos + node.nodeSize
          if (m.type.name === "diffRemoved") { removed += node.text!; hasR = true }
          if (m.type.name === "diffAdded")   { added   += node.text!; hasA = true }
        }
      })
    })
    return { from, to, removed, added, hasR, hasA }
  }

  const replace = (from: number, to: number, txt: string) => {
    const { tr, schema } = editor.state
    txt
      ? editor.view.dispatch(tr.replaceWith(from, to, schema.text(txt)))
      : editor.view.dispatch(tr.delete(from, to))
  }

  /* -------------------------------------------------
     4) Render
  ------------------------------------------------- */
  return (
    <FloatingMenu
      editor={editor}
      className="diff-floating-menu"
      tippyOptions={{ placement: "top", offset: [0, 6] }}
      shouldShow={shouldShowMenu}
    >
      <HStack gap={2}>
        {/* ✔ Accept -------------------------------------------------- */}
        <Button
          size="xs"
          colorScheme="green"
          disabled={!diffId}
          onMouseDown={e => {
            e.preventDefault()
            if (!diffId) return
            const { from, to, removed, added, hasR, hasA } = collectById(diffId)

            if (hasA && !hasR) { replace(from, to, added) }                    // insert
            else if (!hasA && hasR) {                                          // delete
              const prev = editor.state.doc.textBetween(from - 1, from, "")
              const next = editor.state.doc.textBetween(to, to + 1, "")
              const keepSpace = !/\s/.test(prev) && !/\s/.test(next)
              replace(from, to, keepSpace ? " " : "")
            }
            else if (hasA && hasR) {                                           // change
              const lead  = removed.match(/^\s*/)?.[0] ?? ""
              const trail = removed.match(/\s*$/)?.[0] ?? ""
              replace(from, to, `${lead}${added.trim()}${trail}`)
            }
          }}
        >✔ 수용</Button>

        {/* ✘ Reject -------------------------------------------------- */}
        <Button
          size="xs"
          colorScheme="red"
          disabled={!diffId}
          onMouseDown={e => {
            e.preventDefault()
            if (!diffId) return
            const { from, to, removed, hasR, hasA } = collectById(diffId)

            if (hasA && !hasR)       replace(from, to, "")          // reject insert
            else if (!hasA && hasR)  replace(from, to, removed)     // reject delete
            else if (hasA && hasR)   replace(from, to, removed)     // reject change
          }}
        >✘ 기존유지</Button>

        {/* Accept All -------------------------------------------------- */}
        <Button
        size="xs"
        colorScheme="green"
        variant="outline"
        onMouseDown={(e) => {
            e.preventDefault()

            const { doc } = editor.state
            const processed = new Set<string>()

            // Step through all nodes
            doc.descendants((node) => {
            if (!node.isText) return
            node.marks.forEach((m: any) => {
                const diffId = m.attrs?.diffId
                const markName = m.type.name
                if (
                (markName === "diffAdded" || markName === "diffRemoved") &&
                diffId &&
                !processed.has(diffId)
                ) {
                processed.add(diffId)

                // Reuse collectById and replace
                const { from, to, removed, added, hasA, hasR } = collectById(diffId)

                if (hasA && !hasR) {
                    replace(from, to, added)
                } else if (!hasA && hasR) {
                    const prev = doc.textBetween(from - 1, from, "")
                    const next = doc.textBetween(to, to + 1, "")
                    const keepSpace = !/\s/.test(prev) && !/\s/.test(next)
                    replace(from, to, keepSpace ? " " : "")
                } else if (hasA && hasR) {
                    const lead = removed.match(/^\s*/)?.[0] ?? ""
                    const trail = removed.match(/\s*$/)?.[0] ?? ""
                    replace(from, to, `${lead}${added.trim()}${trail}`)
                }
                }
            })
            })
        }}
        >
        ✔ 전체 수용
        </Button>

      </HStack>
    </FloatingMenu>
  )
}
